import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { trackLead } from "@/lib/pixels";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import { COMPANY } from "@/lib/company";
import { Mail, MessageCircle, Send, CheckCircle2 } from "lucide-react";

const WHATSAPP_DIGITS = COMPANY.phone.replace(/[^0-9]/g, "");

export default function Contact() {
  useSeo({
    title: "Contact | PlanPermis",
    description: "Une question ? Contactez l'équipe PlanPermis par formulaire, email ou WhatsApp.",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir ton nom, ton email et ton message.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/public/contact", form);
      // Google Ads : conversion « Envoi de formulaire de lead » (1 €)
      if (data?.id) trackLead(data.id, "contact");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
      toast.success("Message envoyé ! On te répond au plus vite.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="page-contact">
      <header className="border-b border-neutral-200 bg-white px-6 py-4 sm:px-12 lg:px-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" data-testid="contact-home-link">
            <Logo className="h-10" />
          </Link>
          <div className="flex items-center gap-fib-4 text-gr-sm font-semibold">
            <Link to="/blog" data-testid="contact-blog-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Blog</Link>
            <Link to="/ambassadeur" data-testid="contact-ambassador-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir ambassadeur</Link>
            <Link to="/" className="rounded-full bg-brand px-5 py-2 text-white hover:opacity-90">
              Demander une date
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-neutral-900" data-testid="contact-title">
            Une question ? Parlons-en.
          </h1>
          <p className="mt-3 text-neutral-600">
            Écris-nous, on te répond rapidement. Choisis le canal qui te convient.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
          {/* Formulaire */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
            {sent ? (
              <div className="flex flex-col items-center py-12 text-center" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                <h2 className="mt-4 text-xl font-bold text-neutral-900">Message bien reçu !</h2>
                <p className="mt-2 text-neutral-500">Notre équipe revient vers toi dès que possible.</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 rounded-lg border border-neutral-300 px-5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {/* Honeypot anti-robot : invisible pour les humains, rempli par les bots */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>
                    Ne pas remplir
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      data-testid="contact-website-hp"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-semibold text-neutral-800">Nom complet *</span>
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={set("name")}
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                      placeholder="Ton nom"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-semibold text-neutral-800">Email *</span>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={set("email")}
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                      placeholder="ton@email.fr"
                    />
                  </label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-semibold text-neutral-800">Téléphone</span>
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={set("phone")}
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                      placeholder="06 12 34 56 78"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-semibold text-neutral-800">Sujet</span>
                    <input
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={set("subject")}
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                      placeholder="Objet de ta demande"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-sm font-semibold text-neutral-800">Message *</span>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={set("message")}
                    rows={5}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="Explique-nous ta situation…"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {sending ? "Envoi…" : "Envoyer le message"}
                </button>
              </form>
            )}
          </div>

          {/* Coordonnées directes */}
          <div className="space-y-4">
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-5 transition-colors hover:border-brand"
            >
              <div className="rounded-xl bg-brand/10 p-2.5 text-brand">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-neutral-900">Par email</p>
                <p className="mt-0.5 text-sm text-neutral-500">{COMPANY.email}</p>
              </div>
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_DIGITS}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-5 transition-colors hover:border-emerald-500"
            >
              <div className="rounded-xl bg-emerald-50 p-2.5 text-emerald-600">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-neutral-900">Sur WhatsApp</p>
                <p className="mt-0.5 text-sm text-neutral-500">{COMPANY.phone}</p>
              </div>
            </a>
            <div className="rounded-2xl bg-neutral-900 p-5 text-white">
              <p className="text-sm font-bold">Réservation simple &amp; sécurisée</p>
              <p className="mt-1 text-sm text-neutral-300">
                On étudie ta demande à la main et on te met en relation avec la bonne auto-école.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
